﻿<template>
  <div id="app" class="min-h-screen bg-gray-50">
    <ErrorBoundary>
      <!-- 通用导航栏 - 仅在已登录且非登录页时显示 -->
      <UniversalNavBar v-if="showNavBar" />
      
      <router-view />
    </ErrorBoundary>
    
    <!-- Global overlays and modals -->
    <div id="modal-root"></div>
    <div id="toast-root"></div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useDeviceDetection } from '@/composables/device/useDeviceDetection'
import ErrorBoundary from './components/ErrorBoundary.vue'
import UniversalNavBar from './components/UniversalNavBar.vue'

const route = useRoute()
const authStore = useAuthStore()
const { isMobile } = useDeviceDetection()

// Initialize Notification Store and WebSocket listener
import { onMounted, onUnmounted, watch } from 'vue'
import { useNotificationStore } from '@/stores/notification'
import { useStocksStore } from '@/stores/stocks'
import { onErrorCaptured } from 'vue'
import { newsApi } from '@/api/client'

const notificationStore = useNotificationStore()
const stocksStore = useStocksStore()

// Handle visibility change to reconnect WS and sync data
const handleVisibilityChange = async () => {
    if (document.visibilityState === 'visible' && authStore.isAuthenticated) {
        console.log('👀 App became visible, syncing data...')
        
        // 1. Ensure WS connection
        if (!stocksStore.wsConnected) {
            console.log('🔌 Reconnecting WebSocket...')
            stocksStore.connectWebSocket()
        }
        
        // 2. 通过轻量级接口同步未读热点新闻数量
        try {
            const res = await newsApi.getUnreadCount()
            const unreadCount = res.data?.unread_count ?? 0
            console.log('🔴 Synced unread count:', unreadCount)
            notificationStore.setUnreadCount(unreadCount)
        } catch (e) {
            console.error('Failed to sync unread count on visibility change:', e)
        }
    }
}

// Initialize unread count on mount
const initializeUnreadCount = async () => {
    if (authStore.isAuthenticated) {
        try {
            const res = await newsApi.getUnreadCount()
            const unreadCount = res.data?.unread_count ?? 0
            console.log('🔴 Initialized unread count:', unreadCount)
            notificationStore.setUnreadCount(unreadCount)
        } catch (e) {
            console.error('Failed to initialize unread count:', e)
        }
    }
}

// WebSocket handling is now encapsulated in stocksStore
// We just need to ensure it connects when logged in
onMounted(() => {
    // Initial connection
    if (authStore.isAuthenticated) {
        stocksStore.connectWebSocket()
        // Initialize unread count
        initializeUnreadCount()
    }
    
    // Add visibility listener
    document.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
})

// Listen to auth changes to connect/disconnect
watch(() => authStore.isAuthenticated, (isAuthenticated) => {
    if (isAuthenticated) {
        stocksStore.connectWebSocket()
        // Initialize unread count when user logs in
        initializeUnreadCount()
    } else {
        stocksStore.disconnectWebSocket()
        // Clear unread count when user logs out
        notificationStore.clearHotNews()
    }
})

// 判断是否显示导航栏
const showNavBar = computed(() => {
  // 登录页不显示导航栏
  if (route.name === 'Login') return false
  
  // 移动端路由不显示导航栏（移动端有自己的底部导航）
  if (route.path.startsWith('/mobile/')) return false
  
  // 如果是移动端设备且还未进入移动端路由，也先不显示桌面导航栏，避免闪烁
  if (isMobile.value) return false
  
  // 需要登录才显示导航栏
  return authStore.isAuthenticated
})

// 添加全局错误处理
onErrorCaptured((error, instance, info) => {
  console.error('🚨 App.vue捕获全局错误:', error)
  console.error('🚨 错误信息:', info)
  console.error('🚨 组件实例:', instance)
  
  // 让错误继续传播到ErrorBoundary
  return true
})
</script>

<style scoped>
/* Global modal and toast containers */
#modal-root,
#toast-root {
  @apply relative z-50;
}
</style>
